import { FormEvent, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  LinearProgress,
  Link,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { addDays, addMonths, format, isValid, parseISO } from 'date-fns';
import { useMutation } from '@tanstack/react-query';

import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import {
  OverlapSession,
  ProviderConflict,
  ProviderSessionOverlapData,
  fetchProviderSessionOverlaps,
} from '../api/providerSessionOverlaps';
import { bookingsManagementPath } from '@/features/bookings-management/routes/paths';
import { returnSessionFilterFromDate } from '@/features/bookings-management/utils/bookingSessions';
import { ROOTS_JOB_SCHEDULER } from '@/features/job-scheduler/routes/paths';

const formatInputDate = (date: Date) => format(date, 'yyyy-MM-dd');

const parseDateSafe = (value?: string) => {
  if (!value) return null;
  const parsed = parseISO(value);
  if (isValid(parsed)) return parsed;
  const fallback = new Date(value);
  return isValid(fallback) ? fallback : null;
};

const formatDateTime = (value?: string, pattern: string = 'dd MMM yyyy HH:mm') => {
  const parsed = parseDateSafe(value);
  return parsed ? format(parsed, pattern) : undefined;
};

const formatTimeRange = (start?: string, end?: string) => {
  const startLabel = formatDateTime(start, 'HH:mm');
  const endLabel = formatDateTime(end, 'HH:mm');
  if (!startLabel || !endLabel) return 'Unknown overlap time';
  return `${startLabel} – ${endLabel}`;
};

const HIGHLIGHT_PROVIDER_QUERY_PARAM = 'highlightProvider';

const buildSessionLink = (session: OverlapSession) => {
  if (!session.bookingId) return null;
  if (session.sessionId && session.date) {
    return bookingsManagementPath('view', {
      bookingId: String(session.bookingId),
      bookingTabSid: 'booking-sessions',
      sessionFilter: `${returnSessionFilterFromDate(session.date)}?sessionID=${
        session.sessionId
      }`,
    });
  }

  return bookingsManagementPath('view', {
    bookingId: String(session.bookingId),
    bookingTabSid: 'booking-sessions',
  });
};

type SessionSummaryProps = {
  label: string;
  session: OverlapSession;
};

const SessionSummary = ({ label, session }: SessionSummaryProps) => {
  const bookingLink = buildSessionLink(session);
  const bookingLabel = session.bookingId ? `Booking #${session.bookingId}` : 'Booking';
  const sessionLabel = session.sessionId ? `Session #${session.sessionId}` : 'Session';
  const sessionStartLabel =
    formatDateTime(session.startTime, 'dd MMM yyyy HH:mm') ?? 'N/A';
  const sessionEndLabel = formatDateTime(session.endTime, 'dd MMM yyyy HH:mm') ?? 'N/A';

  return (
    <Stack spacing={0.5} flex={1}>
      <Typography variant="subtitle2">{label}</Typography>
      <Typography variant="body2">
        {sessionLabel}
        {session.status ? ` • ${session.status}` : ''}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {sessionStartLabel} – {sessionEndLabel}
      </Typography>
      {session.timeslot && (
        <Typography variant="body2" color="text.secondary">
          Timeslot: {session.timeslot}
        </Typography>
      )}
      {bookingLink && (
        <Link component={RouterLink} to={bookingLink} target="_blank" underline="hover">
          {bookingLabel}
        </Link>
      )}
    </Stack>
  );
};

const useDefaultDateRange = () => {
  return useMemo(() => {
    const tomorrow = addDays(new Date(), 1);
    const formatted = formatInputDate(tomorrow);
    return { start: formatted, end: formatted };
  }, []);
};

const getErrorMessage = (error: unknown) => {
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message?: unknown }).message);
  }
  return 'Unable to fetch overlap report.';
};

export default function ProviderOverlapsPanel() {
  const { errorMsg } = useSnackbarMsg();
  const defaultRange = useDefaultDateRange();
  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  type RangePreset = '2w' | '1m' | '3m' | '6m' | 'custom';
  const [rangePreset, setRangePreset] = useState<RangePreset>('custom');
  const [report, setReport] = useState<ProviderSessionOverlapData | null>(null);
  const [inlineError, setInlineError] = useState<string | null>(null);

  const overlapsMutation = useMutation({
    mutationFn: fetchProviderSessionOverlaps,
    onSuccess: (data) => {
      setReport(data);
      setInlineError(null);
    },
    onError: (error) => {
      setInlineError(getErrorMessage(error));
      errorMsg(error, 'Error fetching overlap report');
    },
  });

  const getBaseStartDate = () =>
    parseDateSafe(startDate) ?? parseDateSafe(defaultRange.start) ?? addDays(new Date(), 1);

  const applyPreset = (preset: RangePreset) => {
    const base = getBaseStartDate();
    let nextEnd = base;
    switch (preset) {
      case '2w':
        nextEnd = addDays(base, 13);
        break;
      case '1m':
        nextEnd = addMonths(base, 1);
        break;
      case '3m':
        nextEnd = addMonths(base, 3);
        break;
      case '6m':
        nextEnd = addMonths(base, 6);
        break;
      case 'custom':
      default:
        nextEnd = parseDateSafe(endDate) ?? base;
        break;
    }

    setStartDate(formatInputDate(base));
    setEndDate(formatInputDate(nextEnd));
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setInlineError(null);
    if (!startDate || !endDate) {
      setInlineError('Please choose both a start and end date.');
      return;
    }
    overlapsMutation.mutate({ startDate, endDate });
  };

  const summaryItems = report
    ? [
        { label: 'Providers checked', value: report.providersChecked },
        { label: 'Providers with conflicts', value: report.providersWithConflicts },
        { label: 'Total conflicts', value: report.totalConflicts },
      ]
    : [];

  return (
    <Card variant="outlined">
      <CardHeader
        title="Session overlap report"
        subheader="Detect providers double-booked over a date range."
      />
      <Divider />
      <CardContent>
        <Stack component="form" spacing={2} onSubmit={handleSubmit}>
          <ToggleButtonGroup
            value={rangePreset}
            exclusive
            onChange={(_e, value: RangePreset | null) => {
              if (!value) return;
              setRangePreset(value);
              if (value !== 'custom') {
                applyPreset(value);
              }
            }}
            size="small"
            fullWidth
          >
            <ToggleButton value="2w">2 weeks</ToggleButton>
            <ToggleButton value="1m">1 month</ToggleButton>
            <ToggleButton value="3m">3 months</ToggleButton>
            <ToggleButton value="6m">6 months</ToggleButton>
            <ToggleButton value="custom">Custom</ToggleButton>
          </ToggleButtonGroup>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ sm: 'flex-end' }}
          >
            <TextField
              label="Start date"
              type="date"
              value={startDate}
              onChange={(e) => {
                const value = e.target.value;
                setStartDate(value);
                if (value && endDate < value) {
                  setEndDate(value);
                }
              }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: defaultRange.start }}
              fullWidth
              disabled={rangePreset !== 'custom'}
            />
            <TextField
              label="End date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: startDate }}
              fullWidth
              disabled={rangePreset !== 'custom'}
            />
          </Stack>

          <LoadingButton
            type="submit"
            variant="contained"
            loading={overlapsMutation.isLoading}
            fullWidth
          >
            Run overlap check
          </LoadingButton>
        </Stack>

        {overlapsMutation.isLoading && <LinearProgress sx={{ mt: 2 }} />}

        {inlineError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {inlineError}
          </Alert>
        )}

        {report && (
          <Stack spacing={2} mt={2}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {summaryItems.map((item) => (
                <Chip
                  key={item.label}
                  label={`${item.label}: ${item.value ?? 0}`}
                  color="primary"
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              ))}
              <Chip
                label={`Range: ${report.startDate} → ${report.endDate}`}
                variant="outlined"
              />
            </Stack>

            <Divider />

            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Overlaps by provider
              </Typography>
              {!report.overlaps?.length ? (
                <Typography color="text.secondary" fontSize={14}>
                  No overlaps found for this date range.
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {report.overlaps.map((provider) => (
                    <Paper key={provider.providerId} variant="outlined" sx={{ p: 1.5 }}>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        spacing={0.5}
                        mb={1}
                      >
                        <Box>
                          <Typography fontWeight={600}>{provider.providerName}</Typography>
                          {provider.providerEmail && (
                            <Typography color="text.secondary" fontSize={13}>
                              {provider.providerEmail}
                            </Typography>
                          )}
                        </Box>
                        <Chip
                          size="small"
                          color="warning"
                          label={`${provider.conflicts.length} conflict${
                            provider.conflicts.length === 1 ? '' : 's'
                          }`}
                        />
                      </Stack>

                      <Stack spacing={1}>
                        {provider.conflicts.map((conflict: ProviderConflict, idx) => {
                          const schedulerLink = getSchedulerLinkForConflict(
                            conflict,
                            provider.providerId
                          );

                          return (
                            <Paper
                              key={`${provider.providerId}-${idx}`}
                              variant="outlined"
                              sx={{ p: 1, bgcolor: 'grey.50' }}
                            >
                              <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={1}
                                alignItems="flex-start"
                              >
                                <SessionSummary label="Session A" session={conflict.sessionA} />
                                <SessionSummary label="Session B" session={conflict.sessionB} />
                              </Stack>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 1 }}
                              >
                                Overlap: {formatTimeRange(
                                  conflict.overlapStart,
                                  conflict.overlapEnd
                                )}
                              </Typography>
                              {schedulerLink && (
                                <Link
                                  component={RouterLink}
                                  to={schedulerLink}
                                  underline="hover"
                                  sx={{ mt: 0.5, display: 'inline-flex' }}
                                >
                                  View day in scheduler
                                </Link>
                              )}
                            </Paper>
                          );
                        })}
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Unresolved sessions
              </Typography>
              {!report.unresolvedSessions?.length ? (
                <Typography color="text.secondary" fontSize={14}>
                  No unresolved sessions reported.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {report.unresolvedSessions.map((session) => (
                    <Paper
                      key={`${session.providerId ?? 'p'}-${session.sessionId ?? 's'}`}
                      variant="outlined"
                      sx={{ p: 1 }}
                    >
                      <Typography fontWeight={600}>
                        Session #{session.sessionId ?? 'N/A'}{' '}
                        {session.providerId ? `• Provider #${session.providerId}` : ''}
                      </Typography>
                      <Typography color="text.secondary" fontSize={13}>
                        {session.reason || 'Missing time data'}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Box>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
const getSchedulerLinkForConflict = (
  conflict: ProviderConflict,
  providerId?: number
) => {
  const dateStr =
    conflict.sessionA.date || conflict.sessionB.date || conflict.overlapStart?.slice(0, 10);
  if (!dateStr) return null;

  const parsed = parseISO(dateStr);
  const parsedDate = isValid(parsed) ? parsed : new Date(dateStr);
  if (!isValid(parsedDate)) return null;

  const formatted = format(parsedDate, 'dd-MM-yyyy');
  const params = new URLSearchParams({ date: formatted });
  if (providerId) {
    params.set(HIGHLIGHT_PROVIDER_QUERY_PARAM, String(providerId));
  }

  return `${ROOTS_JOB_SCHEDULER}?${params.toString()}`;
};
